import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Search, Upload, Tag as TagIcon, CheckSquare, Square, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ProductImage from "@/components/shared/ProductImage";
import { CATEGORIES } from "@/lib/constants";

const BRANDS = ["all", "ASOS", "Mango", "Meski", "New Look", "Next", "Zara", "River Island", "Topshop"];

export default function AdminCatalog() {
    const [products, setProducts] = useState([]);
    const [clients, setClients] = useState([]);
    const [brand, setBrand] = useState("all");
    const [tag, setTag] = useState("");
    const [category, setCategory] = useState("all");
    const [searchParams, setSearchParams] = useSearchParams();
    const uncategorisedMode = searchParams.get("uncategorised") === "1";
    const initialSyncing = searchParams.get("syncing") === "1" || searchParams.get("needs_upload") === "1";
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState(null);
    const [summary, setSummary] = useState({ ready: 0, pending: 0, fetching: 0, needs_upload: 0, uncategorised: 0 });
    const [showSyncing, setShowSyncing] = useState(initialSyncing);
    const [uploadingPhotoFor, setUploadingPhotoFor] = useState("");
    const [dragOverId, setDragOverId] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [bulkCat, setBulkCat] = useState("");
    // Send-to-client lifecycle state
    const [sendTarget, setSendTarget] = useState(null);
    const [sendClientIds, setSendClientIds] = useState(new Set());
    const [sending, setSending] = useState(false);
    // Pagination — preview infra aborts too many concurrent img loads
    const [pageSize, setPageSize] = useState(30);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (brand !== "all") params.brand = brand;
            if (tag) params.tag = tag;
            if (category !== "all") params.category = category;
            if (q) params.q = q;
            if (showSyncing) { params.include_pending = true; params.only_pending = true; }
            if (uncategorisedMode) { params.include_pending = true; params.only_uncategorised = true; }
            // Default view = Catalog (Draft). Once sent, items move to /admin/archive.
            if (!showSyncing && !uncategorisedMode) params.lifecycle = "catalog";
            const r = await api.get("/products", { params });
            setProducts(r.data);
        } catch (e) {
            toast.error("Failed to load products");
        } finally {
            setLoading(false);
        }
    };

    const loadSummary = async () => {
        try {
            const r = await api.get("/products/summary");
            setSummary(r.data);
        } catch (e) { /* silent */ }
    };

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        load();
        loadSummary();
    }, [brand, tag, category, showSyncing, uncategorisedMode]);

    // Refresh summary every 8s while syncing is happening
    useEffect(() => {
        if (summary.fetching === 0) return;
        const t = setInterval(loadSummary, 8000);
        return () => clearInterval(t);
    }, [summary.fetching]);

    const remove = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        await api.delete(`/products/${id}`);
        toast.success("Product removed");
        load(); loadSummary();
    };

    const saveEdit = async () => {
        try {
            await api.put(`/products/${edit.id}`, {
                title: edit.title,
                brand: edit.brand,
                price: parseFloat(edit.price),
                currency: edit.currency,
                description: edit.description,
                category: edit.category,
                tags: edit.tags || [],
                visible_to: edit.visible_to || [],
            });
            toast.success("Product updated");
            setEdit(null);
            load(); loadSummary();
        } catch (e) {
            toast.error("Update failed");
        }
    };

    const toggleTag = (t) => {
        if (!edit) return;
        const has = (edit.tags || []).includes(t);
        setEdit({ ...edit, tags: has ? edit.tags.filter((x) => x !== t) : [...(edit.tags || []), t] });
    };

    const uploadPhoto = useCallback(async (productId, file) => {
        if (!file || !productId) return;
        if (!file.type?.startsWith("image/")) {
            toast.error("Please pick an image file");
            return;
        }
        setUploadingPhotoFor(productId);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/products/${productId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Photo uploaded");
            load(); loadSummary();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadingPhotoFor("");
        }
    }, [load]);

    const handleDrop = (e, productId) => {
        e.preventDefault(); e.stopPropagation();
        setDragOverId("");
        const file = e.dataTransfer?.files?.[0];
        if (file) uploadPhoto(productId, file);
    };

    const toggleSelected = (id) => {
        setSelected((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const applyBulkCategory = async () => {
        if (!bulkCat || selected.size === 0) return;
        const body = {};
        selected.forEach((id) => { body[id] = bulkCat; });
        try {
            const r = await api.post("/admin/categorise-bulk", body);
            toast.success(`Assigned ${r.data.updated} · ${bulkCat}`);
            setSelected(new Set()); setBulkCat("");
            load(); loadSummary();
        } catch (e) {
            toast.error("Bulk assign failed");
        }
    };

    const clearUncatMode = () => {
        const p = new URLSearchParams(searchParams);
        p.delete("uncategorised");
        setSearchParams(p);
        setSelected(new Set());
    };

    const openSend = (product) => {
        setSendTarget(product);
        setSendClientIds(new Set());
    };

    const toggleSendClient = (id) => {
        setSendClientIds((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const doSend = async () => {
        if (!sendTarget || sendClientIds.size === 0) return;
        setSending(true);
        try {
            await api.post(`/products/${sendTarget.id}/send`, { client_ids: Array.from(sendClientIds) });
            toast.success(`Sent to ${sendClientIds.size} client${sendClientIds.size === 1 ? "" : "s"} · moved to Archive → Sent`);
            setSendTarget(null);
            setSendClientIds(new Set());
            load(); loadSummary();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
            <div className="flex items-end justify-between mb-6 sm:mb-8">
                <div>
                    <div className="overline text-neutral-500">Inventory</div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none mt-2">
                        {uncategorisedMode ? "Needs Category" : "Catalog"}
                    </h1>
                </div>
                <div className="font-body text-sm text-neutral-500 text-right">
                    <div><span className="font-semibold text-black tabular-nums">{summary.ready}</span> ready</div>
                    {summary.fetching > 0 && (
                        <div className="text-xs text-neutral-500 tabular-nums">{summary.fetching} syncing</div>
                    )}
                    {summary.needs_upload > 0 && (
                        <div className="text-xs text-red-700 tabular-nums font-semibold">{summary.needs_upload} need photo</div>
                    )}
                </div>
            </div>

            {uncategorisedMode && (
                <div className="mb-4 border border-yellow-500 bg-yellow-50 px-4 py-3 flex flex-wrap items-center gap-3 text-xs" data-testid="uncat-banner">
                    <TagIcon className="h-4 w-4 text-yellow-700" strokeWidth={1.5}/>
                    <span className="font-body text-neutral-800 flex-1 min-w-[180px]">
                        <span className="font-semibold">Bulk-assign mode.</span> Tick the items that fit one category, choose the category and hit Apply. Repeat for other categories.
                    </span>
                    <button type="button" onClick={clearUncatMode} className="uppercase tracking-widest text-[10px] font-semibold border border-black px-3 h-8 hover:bg-black hover:text-white" data-testid="exit-uncat-mode">
                        Exit mode
                    </button>
                </div>
            )}

            {uncategorisedMode && (
                <div className="mb-4 border border-neutral-300 bg-white px-4 py-3 flex flex-wrap items-center gap-3 sticky top-14 md:top-16 z-30" data-testid="bulk-cat-bar">
                    <span className="overline text-neutral-500">Selected</span>
                    <span className="font-display text-lg tabular-nums" data-testid="bulk-selected-count">{selected.size}</span>
                    <button
                        type="button"
                        onClick={() => setSelected(new Set(products.map((p) => p.id)))}
                        className="uppercase tracking-widest text-[10px] font-semibold border border-black px-3 h-8 hover:bg-black hover:text-white"
                        data-testid="bulk-select-all"
                    >Select all shown</button>
                    <button
                        type="button"
                        onClick={() => setSelected(new Set())}
                        className="uppercase tracking-widest text-[10px] font-semibold border border-neutral-300 px-3 h-8 hover:border-black"
                        data-testid="bulk-clear"
                    >Clear</button>
                    <div className="flex-1 min-w-[120px]"/>
                    <Select value={bulkCat} onValueChange={setBulkCat}>
                        <SelectTrigger className="w-44 rounded-none border-black h-9" data-testid="bulk-cat-picker">
                            <SelectValue placeholder="Pick category…"/>
                        </SelectTrigger>
                        <SelectContent>
                            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <button
                        type="button"
                        disabled={!bulkCat || selected.size === 0}
                        onClick={applyBulkCategory}
                        data-testid="bulk-apply"
                        className="uppercase tracking-widest text-[10px] font-semibold bg-black text-white px-4 h-9 hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Apply to {selected.size} item{selected.size === 1 ? "" : "s"}
                    </button>
                </div>
            )}

            {!uncategorisedMode && (summary.pending > 0 || summary.needs_upload > 0) && (
                <div className="mb-4 border border-neutral-300 bg-neutral-50 px-4 py-2.5 flex flex-wrap items-center gap-3 text-xs" data-testid="catalog-syncing-banner">
                    <span className={`inline-block w-2 h-2 rounded-full ${summary.fetching > 0 ? "bg-black animate-pulse" : "bg-red-600"}`}></span>
                    <span className="font-body text-neutral-700 flex-1 min-w-[180px]">
                        {summary.fetching > 0 && <><span className="font-semibold">{summary.fetching}</span> syncing in background · </>}
                        {summary.needs_upload > 0 && <><span className="font-semibold text-red-700">{summary.needs_upload} need a manual photo upload</span> (drag &amp; drop onto the card, or tap Photo)</>}
                    </span>
                    <button
                        type="button"
                        onClick={() => setShowSyncing(!showSyncing)}
                        data-testid="toggle-syncing-view"
                        className="uppercase tracking-widest text-[10px] font-semibold border border-black px-3 h-8 hover:bg-black hover:text-white"
                    >
                        {showSyncing ? "Show ready items" : "Review syncing items"}
                    </button>
                </div>
            )}

            <div className="flex flex-wrap gap-2 sm:gap-3 items-center mb-6 border-t border-b border-border py-3 sm:py-4">
                <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 flex-1 min-w-[240px]">
                    <div className="relative flex-1 max-w-md">
                        <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5}/>
                        <Input
                            data-testid="catalog-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search title…"
                            className="pl-9 rounded-none h-10 border-black"
                        />
                    </div>
                </form>

                <Select value={brand} onValueChange={setBrand}>
                    <SelectTrigger className="w-32 sm:w-40 rounded-none border-black" data-testid="filter-brand">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {BRANDS.map((b) => <SelectItem key={b} value={b}>{b === "all" ? "All Brands" : b}</SelectItem>)}
                    </SelectContent>
                </Select>

                <Select value={tag || "all"} onValueChange={(v) => setTag(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-32 sm:w-44 rounded-none border-black" data-testid="filter-tag">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Tags</SelectItem>
                        <SelectItem value="new_arrival">New Arrival</SelectItem>
                        <SelectItem value="best_seller">Best Seller</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="w-36 sm:w-48 rounded-none border-black" data-testid="filter-category">
                        <SelectValue/>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {loading && <div className="text-sm text-neutral-500 font-body">Loading…</div>}

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {products.map((p, idx) => {
                    // Only show the "needs upload" overlay when there's genuinely
                    // NO image URL. If a URL exists the actual photo renders —
                    // the manual_needed flag is stale (auto-backfill clears it).
                    const needsUpload = !!p.image_manual_needed && !p.image_url;
                    const isSyncing = !!p.image_pending && !needsUpload && !p.image_url;
                    const isSelected = selected.has(p.id);
                    return (
                    <div
                        key={p.id}
                        className={`brutal-card group stagger-item ${dragOverId === p.id ? "ring-2 ring-black" : ""} ${isSelected ? "ring-2 ring-yellow-500" : ""}`}
                        style={{ animationDelay: `${idx * 30}ms` }}
                        data-testid={`product-card-${p.id}`}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(p.id); }}
                        onDragLeave={() => setDragOverId("")}
                        onDrop={(e) => handleDrop(e, p.id)}
                    >
                        <div className="aspect-[3/4] bg-neutral-50 border-b border-border overflow-hidden relative">
                            {uncategorisedMode && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleSelected(p.id); }}
                                    className={`absolute top-2 right-2 z-10 w-7 h-7 border-2 border-black flex items-center justify-center ${isSelected ? "bg-yellow-400" : "bg-white/90"}`}
                                    data-testid={`bulk-tick-${p.id}`}
                                >
                                    {isSelected ? <CheckSquare className="h-4 w-4"/> : <Square className="h-4 w-4"/>}
                                </button>
                            )}
                            <ProductImage product={p} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                            {needsUpload && (
                                <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center gap-2 pointer-events-none">
                                    <Upload className="h-6 w-6 text-red-700" strokeWidth={1.5}/>
                                    <div className="text-[10px] uppercase tracking-widest font-semibold text-red-700 text-center px-2">
                                        Drop photo here<br/>or tap Photo
                                    </div>
                                </div>
                            )}
                            <div className="absolute top-3 left-3 flex flex-col gap-1">
                                {(p.tags || []).includes("new_arrival") && (
                                    <Badge variant="outline" className="rounded-none uppercase tracking-widest text-[9px] bg-white border-black">New Arrival</Badge>
                                )}
                                {(p.tags || []).includes("best_seller") && (
                                    <Badge variant="outline" className="rounded-none uppercase tracking-widest text-[9px] bg-yellow-400 border-black">Best Seller</Badge>
                                )}
                                {needsUpload && (
                                    <Badge variant="outline" className="rounded-none uppercase tracking-widest text-[9px] bg-red-600 text-white border-red-700">No photo</Badge>
                                )}
                                {isSyncing && (
                                    <Badge variant="outline" className="rounded-none uppercase tracking-widest text-[9px] bg-white border-neutral-400">Syncing</Badge>
                                )}
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="overline text-neutral-500" data-testid={`card-sku-${p.id}`}>
                                    <span className="text-neutral-400">{p.brand || "—"}</span>
                                    {p.sku_code && <span className="text-black ml-2 font-mono">{p.sku_code}</span>}
                                </div>
                                {p.category ? (
                                    <div className="overline text-neutral-400 text-[9px]" data-testid={`card-cat-${p.id}`}>{p.category}</div>
                                ) : (
                                    <div className="overline text-yellow-700 text-[9px] font-semibold" data-testid={`card-no-cat-${p.id}`}>No cat</div>
                                )}
                            </div>
                            <div className="font-body text-sm mt-1 line-clamp-2 min-h-[2.5em]">{p.title}</div>
                            {(p.visible_to || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2" data-testid={`card-visibility-${p.id}`}>
                                    {p.visible_to.map((cid) => {
                                        const c = clients.find((x) => x.id === cid);
                                        if (!c) return null;
                                        return (
                                            <span key={cid} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 text-white" style={{ background: c.brand_color || "#0A0A0A" }}>
                                                {c.brand_display_name || c.company || c.login_id}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            {(!p.visible_to || p.visible_to.length === 0) && (
                                <div className="text-[9px] uppercase tracking-widest text-neutral-400 mt-2">All Clients</div>
                            )}
                            <div className="flex items-center justify-between mt-3">
                                <div>
                                    <div className="font-display text-xl tabular-nums leading-none">{p.currency === "GBP" ? "£" : p.currency === "USD" ? "$" : p.currency === "EUR" ? "€" : ""}{p.price?.toFixed(2)}<span className="text-[10px] uppercase tracking-widest text-neutral-500 ml-1">/pc</span></div>
                                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-1">MOQ 200 pcs</div>
                                </div>
                                <div className="flex items-center gap-1">
                                    {!uncategorisedMode && !showSyncing && !needsUpload && (
                                        <button
                                            type="button"
                                            onClick={() => openSend(p)}
                                            className="rounded-none uppercase tracking-widest text-[10px] font-semibold border border-black bg-black text-white px-2 h-8 flex items-center gap-1 hover:bg-neutral-800"
                                            data-testid={`send-btn-${p.id}`}
                                            title="Send this design to a client"
                                        >
                                            <Send className="h-3 w-3" strokeWidth={1.5}/> Send
                                        </button>
                                    )}
                                    {(needsUpload || isSyncing) && (
                                        <label
                                            className={`cursor-pointer rounded-none uppercase tracking-widest text-[10px] font-semibold border px-2 h-8 flex items-center ${needsUpload ? "border-red-700 text-red-700 hover:bg-red-700 hover:text-white" : "border-black hover:bg-black hover:text-white"}`}
                                            data-testid={`upload-photo-${p.id}`}
                                            title="Upload photo"
                                        >
                                            {uploadingPhotoFor === p.id ? "…" : "Photo"}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => uploadPhoto(p.id, e.target.files?.[0])}
                                                disabled={uploadingPhotoFor === p.id}
                                            />
                                        </label>
                                    )}
                                    <Button variant="ghost" size="sm" data-testid={`edit-btn-${p.id}`} className="rounded-none uppercase tracking-widest text-[10px]" onClick={() => setEdit({ ...p, tags: p.tags || [] })}>Edit</Button>
                                    <Button variant="ghost" size="icon" data-testid={`delete-btn-${p.id}`} className="rounded-none text-red-700 hover:text-red-900 h-8 w-8" onClick={() => remove(p.id)}>
                                        <Trash2 className="h-4 w-4" strokeWidth={1.5}/>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                    );
                })}
            </div>
            {products.length > pageSize && (
                <div className="mt-8 flex flex-col items-center gap-2">
                    <div className="text-xs font-body text-neutral-500 tabular-nums">
                        Showing <span className="font-semibold text-black">{pageSize}</span> of {products.length}
                    </div>
                    <button
                        type="button"
                        onClick={() => setPageSize((s) => s + 30)}
                        className="uppercase tracking-widest text-[11px] font-semibold border-2 border-black px-6 h-11 hover:bg-black hover:text-white"
                        data-testid="load-more-btn"
                    >
                        Load 30 more
                    </button>
                </div>
            )}
            {!loading && products.length === 0 && (
                <div className="border border-dashed border-border p-12 text-center">
                    <div className="overline text-neutral-500">Empty</div>
                    <div className="font-display text-2xl mt-2">
                        {uncategorisedMode ? "All items are categorised" : showSyncing ? "Nothing is syncing" : "No products yet"}
                    </div>
                    <div className="text-sm text-neutral-500 mt-2 font-body">
                        {uncategorisedMode ? "Well done — the dashboard tile will disappear." : showSyncing ? "Every product has a photo. Head back to the ready view." : "Head to \"Add Product\" and paste a brand URL to import."}
                    </div>
                </div>
            )}

            <Dialog open={!!sendTarget} onOpenChange={(open) => !open && setSendTarget(null)}>
                <DialogContent className="rounded-none max-w-md" data-testid="send-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl tracking-tight">Send to client</DialogTitle>
                    </DialogHeader>
                    {sendTarget && (
                        <div className="space-y-4">
                            <div className="border border-border p-3 flex items-center gap-3">
                                <div className="w-14 h-14 shrink-0 bg-neutral-50 border border-border overflow-hidden">
                                    <ProductImage product={sendTarget} className="w-full h-full object-cover"/>
                                </div>
                                <div className="min-w-0">
                                    <div className="overline text-neutral-500">
                                        <span className="text-neutral-400">{sendTarget.brand || "—"}</span>
                                        {sendTarget.sku_code && <span className="text-black ml-2 font-mono">{sendTarget.sku_code}</span>}
                                    </div>
                                    <div className="font-body text-xs mt-1 line-clamp-2">{sendTarget.title}</div>
                                </div>
                            </div>
                            <div>
                                <Label className="overline mb-2 block">Which client(s)?</Label>
                                <div className="flex flex-wrap gap-2" data-testid="send-client-list">
                                    {clients.map((c) => {
                                        const on = sendClientIds.has(c.id);
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => toggleSendClient(c.id)}
                                                data-testid={`send-pick-${c.login_id}`}
                                                style={on ? { background: c.brand_color || "#0A0A0A", borderColor: c.brand_color || "#0A0A0A", color: "#fff" } : {}}
                                                className={`px-3 py-2 border uppercase tracking-widest text-[10px] font-semibold ${on ? "" : "border-black text-black hover:bg-neutral-100"}`}
                                            >
                                                {c.brand_display_name || c.company || c.name} · {c.login_id}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-3">
                                    Once sent, this design leaves the Catalog and lands in Archive → Sent. You can Rollback anytime from there.
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSendTarget(null)} className="rounded-none border-black">Cancel</Button>
                        <Button
                            onClick={doSend}
                            disabled={sendClientIds.size === 0 || sending}
                            className="rounded-none bg-black text-white hover:bg-neutral-800 disabled:opacity-40"
                            data-testid="send-confirm-btn"
                        >
                            {sending ? "Sending…" : `Send to ${sendClientIds.size || "…"} client${sendClientIds.size === 1 ? "" : "s"}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!edit} onOpenChange={(open) => !open && setEdit(null)}>
                <DialogContent className="rounded-none max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl tracking-tight">Edit Product</DialogTitle>
                    </DialogHeader>
                    {edit && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <Label className="overline">Title</Label>
                                <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="rounded-none border-black mt-1" data-testid="edit-title-input"/>
                            </div>
                            <div>
                                <Label className="overline">Brand</Label>
                                <Select value={edit.brand} onValueChange={(v) => setEdit({ ...edit, brand: v })}>
                                    <SelectTrigger className="rounded-none border-black mt-1" data-testid="edit-brand-select"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {BRANDS.filter(b => b !== "all").map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="overline">SKU Code</Label>
                                <Input
                                    value={edit.sku_code || ""}
                                    onChange={(e) => setEdit({ ...edit, sku_code: e.target.value.toUpperCase() })}
                                    className="rounded-none border-black mt-1 font-mono"
                                    placeholder="AVC-000001"
                                    data-testid="edit-sku-input"
                                />
                            </div>
                            <div>
                                <Label className="overline">Price</Label>
                                <Input type="number" step="0.01" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} className="rounded-none border-black mt-1" data-testid="edit-price-input"/>
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="overline">Category</Label>
                                <Select value={edit.category || undefined} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                                    <SelectTrigger className="rounded-none border-black mt-1" data-testid="edit-category-select"><SelectValue placeholder="Select category"/></SelectTrigger>
                                    <SelectContent>
                                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="overline">Description</Label>
                                <Textarea value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="rounded-none border-black mt-1" rows={3}/>
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="overline mb-2 block">Tags</Label>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => toggleTag("new_arrival")} className={`px-3 py-1 border uppercase tracking-widest text-[10px] ${(edit.tags||[]).includes("new_arrival") ? "bg-black text-white border-black" : "border-black text-black hover:bg-neutral-100"}`} data-testid="toggle-tag-new">New Arrival</button>
                                    <button type="button" onClick={() => toggleTag("best_seller")} className={`px-3 py-1 border uppercase tracking-widest text-[10px] ${(edit.tags||[]).includes("best_seller") ? "bg-yellow-400 border-black" : "border-black text-black hover:bg-neutral-100"}`} data-testid="toggle-tag-best">Best Seller</button>
                                </div>
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="overline mb-2 block">Push to Clients</Label>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setEdit({ ...edit, visible_to: [] })}
                                        className={`px-3 py-1 border uppercase tracking-widest text-[10px] ${(edit.visible_to || []).length === 0 ? "bg-black text-white border-black" : "border-black text-black hover:bg-neutral-100"}`}
                                        data-testid="edit-visible-all"
                                    >
                                        All Clients
                                    </button>
                                    {clients.map((c) => {
                                        const on = (edit.visible_to || []).includes(c.id);
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => {
                                                    const cur = edit.visible_to || [];
                                                    const has = cur.includes(c.id);
                                                    setEdit({ ...edit, visible_to: has ? cur.filter((x) => x !== c.id) : [...cur, c.id] });
                                                }}
                                                data-testid={`edit-visible-client-${c.login_id}`}
                                                style={on ? { background: c.brand_color || "#0A0A0A", borderColor: c.brand_color || "#0A0A0A", color: "#fff" } : {}}
                                                className={`px-3 py-1 border uppercase tracking-widest text-[10px] ${on ? "" : "border-black text-black hover:bg-neutral-100"}`}
                                            >
                                                {c.brand_display_name || c.company || c.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEdit(null)} className="rounded-none border-black">Cancel</Button>
                        <Button onClick={saveEdit} className="rounded-none bg-black text-white hover:bg-neutral-800" data-testid="edit-save-btn">Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
